import { Component, Input, OnChanges } from '@angular/core';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { UserService } from '@services/admin/user.service';
import { ToastrService } from 'ngx-toastr';
import { TestDriveFormMeta } from '@/form-settings/test-drive/test-drive-form-meta';
import { LoggerService } from '@services/logger.service';
import { Registry } from '@/registry.service';
import { TestDriveService } from '@services/test-drive.service';
import { TestDriveModel } from '@/models/test-drive/test-drive.model';
import { VehicleSearchModel } from '@/models/test-drive/vehicle-search.model';
import { ClienteSearchModel } from '@/models/test-drive/cliente-search.model';
import { Router } from '@angular/router';

@Component({
  selector: 'app-test-drive-form',
  templateUrl: '../../../../components/dynamic-form/dynamic-form.component.html',
  styleUrls: ['../../../../components/dynamic-form/dynamic-form.component.scss'],
})
export class TestDriveFormComponent extends DynamicFormComponent implements OnChanges {
  @Input() isNewForm: boolean = false;
  @Input() data: TestDriveModel;
  private vehicleSelected: boolean = false;

  constructor(
    protected userService: UserService,
    protected toastr: ToastrService,
    private testDriveService: TestDriveService,
    private router: Router,
  ) {
    super(toastr, userService);
    this.submitTopButton = false;
    this.submitBottomButton = true;
    this.submitButtonVisible = true;
    this.primaryButton = `Salva`;

    if (this.isNewForm) {
      this.primaryButton = `Crea Test Drive`;
    }

    this.setupMeta();
  }

  ngOnChanges() {
    super.ngOnChanges();

    // Se ci sono dati esistenti, popola il veicolo selezionato
    if (this.data?.marca) {
      this.vehicleSelected = true;
      // Se abbiamo già un veicolo selezionato, ricostruiamo l'oggetto per il save
      if (this.data.marca && typeof this.data.marca === 'object') {
        this.selectedVehicle = {
          id: 0, // Non abbiamo l'ID del veicolo dal test drive
          marca: this.data.marca,
          codicePratica: this.data.codicePratica || '',
          modello: this.data.modello || '',
          targa: this.data.targa || '',
          bpCliente: this.data.bpCliente,
          denominazioneCliente: this.data.denominazioneCliente,
        };

        // Popola i campi del form con i dati esistenti
        if (this.form) {
          this.form.patchValue({
            marca: this.data.marca.descrizioneMarca || '',
            modello: this.data.modello || '',
            targa: this.data.targa || '',
            bpCliente: this.data.bpCliente,
            denominazioneCliente: this.data.denominazioneCliente || '',
          });
        }
      }
      this.disableVehicleFields();
    }
  }

  setupMeta(): void {
    this.meta = TestDriveFormMeta.setIsNewForm(this.isNewForm)
      .setFormInstance(this)
      .setClassToCall('TestDriveFormComponent')
      .getMeta();

    Registry.getInstance().set('TestDriveFormComponent', this);
  }

  private selectedVehicle: VehicleSearchModel;

  onClienteSelected(cliente: ClienteSearchModel) {
    if (cliente && (cliente.id || cliente.numeroBp)) {
      // Popola i campi con i dati del cliente selezionato
      this.form.patchValue({
        denominazioneCliente: cliente.denominazione || '',
        dataNascita: cliente.dataNascita || null,
        luogoNascita: cliente.luogoNascita || '',
        numeroPatente: cliente.numeroPatente || '',
        patenteRilasciataDa: cliente.patenteRilasciataDa || '',
        patenteRilasciataIl: cliente.patenteRilasciataIl || null,
        bpCliente: cliente.numeroBp || cliente.bpCliente || null,
      });
    }
  }

  onVehicleSelected(vehicle: VehicleSearchModel) {
    if (vehicle && vehicle.id) {
      this.vehicleSelected = true;
      // Salva il veicolo selezionato per usarlo nel save
      this.selectedVehicle = vehicle;

      // Popola i campi con i dati del veicolo selezionato
      this.form.patchValue({
        marca: vehicle.marca.descrizioneMarca,
        modello: vehicle.descrizioneModello || vehicle.modello,
        targa: vehicle.targa,
        telaio: vehicle.telaio,
        praticaId: vehicle.id,
      });

      this.disableVehicleFields();
    }
  }

  private disableVehicleFields() {
    const marcaControl = this.form.get('marca');
    const modelloControl = this.form.get('modello');
    //const targaControl = this.form.get('targa');
    const telaioControl = this.form.get('telaio');

    if (marcaControl) {
      marcaControl.disable();
    }
    if (modelloControl) {
      modelloControl.disable();
    }
    if (telaioControl) {
      telaioControl.disable();
    }
  }

  onSubmit() {
    // Validazione veicolo selezionato
    if (!this.vehicleSelected && !this.data?.marca) {
      const veicoloControl = this.form.get('veicoloId');
      if (veicoloControl) {
        veicoloControl.setErrors({ required: true });
        veicoloControl.markAsTouched();
      }
      this.toastr.error('È necessario selezionare un veicolo!');
      Registry.getInstance().set('saveInProgress', false);
      return;
    }

    this.submitButtonLoading = true;

    const formValue = this.form.getRawValue();

    const testDrive: TestDriveModel = {
      id: formValue.id,
      dataInizioTestDrive: formValue.dataInizioTestDrive,
      durataMinuti: formValue.durataMinuti || 0,
      marca: this.selectedVehicle?.marca || this.data?.marca,
      modello: formValue.modello,
      targa: formValue.targa,
      telaio: formValue.telaio,
      bpCliente: formValue.bpCliente || null,
      denominazioneCliente: formValue.denominazioneCliente || '',
      dataNascita: formValue.dataNascita,
      luogoNascita: formValue.luogoNascita,
      numeroPatente: formValue.numeroPatente,
      patenteRilasciataDa: formValue.patenteRilasciataDa,
      patenteRilasciataIl: formValue.patenteRilasciataIl,
      luogoContratto: formValue.luogoContratto || null,
      dataContratto: formValue.dataContratto || null,
      praticaId: formValue.praticaId,
    };

    this.testDriveService.saveTestDrive(testDrive).subscribe({
      next: (response: any) => {
        this.toastr.success('Test Drive salvato correttamente!');

        this.submitButtonLoading = false;

        const testDriveId = response?.id || testDrive.id;
        if (this.isNewForm && testDriveId) {
          this.router.navigate([`/test-drive/detail/${testDriveId}`]);
        }
      },
      error: (error) => {
        LoggerService.error('saveTestDrive failed!');
        LoggerService.error(error);

        this.toastr.error(`Errore durante il salvataggio del test drive.`);

        this.submitButtonLoading = false;

        Registry.getInstance().set('saveInProgress', false);
      },
      complete: () => {
        LoggerService.log('Request done');

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
        }, 1000);
      },
    });
  }
}
